import React, { useEffect, useMemo, useRef, useState } from "react";
import "./FinanceSpreadsheetWorkroom.css";
import { C, type DivisionDefinition } from "../commandCenterData";

export interface FinanceNumberSource {
  id: string;
  label: string;
  sourceType: "chat" | "workspace" | "upload" | "manual" | "agent";
  status: "available" | "setup_required" | "empty";
  values?: Array<{ label: string; value: number; unit?: string }>;
}

export interface FinanceSpreadsheetWorkroomProps {
  onBack?: () => void;
  /** Q home — returns to the main Square dashboard (/square/dashboard). */
  onHome?: () => void;
  sourceDivision?: DivisionDefinition | null;
  numberSources?: FinanceNumberSource[];
  onExportXlsx?: (sheet: SpreadsheetScenario) => void;
  onSaveToSystem?: (sheet: SpreadsheetScenario) => void;
  onShare?: (sheet: SpreadsheetScenario) => void;
  onSendToGoogleSheets?: (sheet: SpreadsheetScenario) => void;
}

export interface SpreadsheetRow {
  cells: string[];
  format?: { bold?: boolean; color?: string };
}

export interface SpreadsheetFormula {
  name: string;
  formula: string;
  desc: string;
}

export interface SpreadsheetScenario {
  label: string;
  prompt: string;
  headers: string[];
  rows: SpreadsheetRow[];
  summary: Record<string, string>;
  formulas: SpreadsheetFormula[];
  chart: {
    type: "bar" | "line";
    title: string;
    xLabel: string;
    points: Array<{ label: string; value: number }>;
  };
}

const COLS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const DEFAULT_SOURCES: FinanceNumberSource[] = [
  {
    id: "chat-numbers",
    label: "Chat numbers",
    sourceType: "chat",
    status: "setup_required",
    values: [],
  },
  {
    id: "workspace-uploads",
    label: "Workspace uploads",
    sourceType: "upload",
    status: "setup_required",
    values: [],
  },
  {
    // No Finance agent exists yet, so this source has nothing real to surface.
    // Kept at setup_required with no values — never present fabricated CAC/GM/
    // Runway as live agent findings. Wire to the real agent to populate.
    id: "agent-findings",
    label: "Finance agent findings",
    sourceType: "agent",
    status: "setup_required",
    values: [],
  },
];

const SCENARIOS: SpreadsheetScenario[] = [
  {
    label: "Revenue Forecast",
    prompt: "Create a quarterly revenue forecast for 2026 with 3 product lines. Show unit sales, unit price, revenue per line, totals, and growth.",
    headers: ["Product Line", "Q1 Units", "Q1 Rev", "Q2 Units", "Q2 Rev", "Q3 Units", "Q3 Rev", "Q4 Units", "Q4 Rev", "FY Total"],
    rows: [
      { cells: ["SaaS Platform", "120", "$48K", "180", "$72K", "240", "$96K", "310", "$124K", "$340K"], format: { bold: true, color: C.BLUE } },
      { cells: ["API Access", "45", "$22.5K", "68", "$34K", "95", "$47.5K", "130", "$65K", "$169K"], format: { color: C.GREEN } },
      { cells: ["Consulting", "8", "$32K", "12", "$48K", "15", "$60K", "18", "$72K", "$212K"], format: { color: C.AMBER } },
      { cells: ["", "", "", "", "", "", "", "", "", ""] },
      { cells: ["Total Revenue", "", "$102.5K", "", "$154K", "", "$203.5K", "", "$261K", "$721K"], format: { bold: true, color: C.GREEN } },
      { cells: ["QoQ Growth", "", "—", "", "+50.2%", "", "+32.1%", "", "+28.3%", ""], format: { color: C.CYAN } },
    ],
    summary: { fyRevenue: "$721K", avgGrowth: "36.9% QoQ", topLine: "SaaS Platform" },
    formulas: [
      { name: "Quarterly Revenue", formula: "=units * unit_price", desc: "Revenue per product line" },
      { name: "QoQ Growth", formula: "=(Q_current - Q_prev) / Q_prev", desc: "Quarter over quarter" },
      { name: "FY Total", formula: "=SUM(Q1:Q4)", desc: "Full-year rollup" },
    ],
    chart: {
      type: "bar",
      title: "Revenue by quarter",
      xLabel: "Quarter",
      points: [
        { label: "Q1", value: 102500 },
        { label: "Q2", value: 154000 },
        { label: "Q3", value: 203500 },
        { label: "Q4", value: 261000 },
      ],
    },
  },
  {
    label: "Runway Model",
    prompt: "Build a runway model using cash on hand, monthly burn, hiring plan, credits, and conservative/aggressive scenarios.",
    headers: ["Month", "Cash Start", "Revenue", "Payroll", "Cloud", "Ops", "Net Burn", "Cash End", "Runway"],
    rows: [
      { cells: ["March", "$625K", "$58.8K", "$128K", "$12K", "$34K", "-$115.2K", "$509.8K", "9.2 mo"], format: { color: C.GREEN } },
      { cells: ["April", "$509.8K", "$72K", "$142K", "$14K", "$36K", "-$120K", "$389.8K", "7.9 mo"], format: {} },
      { cells: ["May", "$389.8K", "$86K", "$149K", "$15K", "$38K", "-$116K", "$273.8K", "6.6 mo"], format: {} },
      { cells: ["June", "$273.8K", "$104K", "$156K", "$18K", "$41K", "-$111K", "$162.8K", "5.3 mo"], format: { color: C.AMBER } },
      { cells: ["With Azure Credits", "$625K", "$58.8K", "$128K", "$0", "$34K", "-$103.2K", "$521.8K", "12.8 mo"], format: { bold: true, color: C.CYAN } },
    ],
    summary: { currentRunway: "9.2 mo", creditAdjusted: "12.8 mo", monthlyBurn: "$115K" },
    formulas: [
      { name: "Net Burn", formula: "=revenue - payroll - cloud - ops", desc: "Monthly net cash change" },
      { name: "Cash End", formula: "=cash_start + net_burn", desc: "Ending cash by month" },
      { name: "Runway", formula: "=cash_end / abs(net_burn)", desc: "Months of runway" },
    ],
    chart: {
      type: "line",
      title: "Cash runway trend",
      xLabel: "Month",
      points: [
        { label: "Mar", value: 509800 },
        { label: "Apr", value: 389800 },
        { label: "May", value: 273800 },
        { label: "Jun", value: 162800 },
      ],
    },
  },
  {
    label: "Employee Costs",
    prompt: "Calculate total employee costs for a 12-person team. Include salary, benefits, payroll tax, overhead, and all-in annual cost.",
    headers: ["Role", "Count", "Base Salary", "Benefits", "Tax", "Overhead", "Total Cost"],
    rows: [
      { cells: ["Senior Engineer", "3", "$165,000", "$36,300", "$12,622", "$2,400", "$649,968"], format: { color: C.BLUE } },
      { cells: ["Mid Engineer", "4", "$125,000", "$27,500", "$9,562", "$2,400", "$657,850"], format: {} },
      { cells: ["Designer", "2", "$110,000", "$24,200", "$8,415", "$2,400", "$290,030"], format: {} },
      { cells: ["PM", "1", "$135,000", "$29,700", "$10,327", "$2,400", "$177,427"], format: {} },
      { cells: ["Ops / Admin", "2", "$72,000", "$15,840", "$5,508", "$2,400", "$191,496"], format: {} },
      { cells: ["TOTAL", "12", "", "", "", "", "$1,966,771"], format: { bold: true, color: C.GREEN } },
    ],
    summary: { totalCost: "$1,966,771", avgEmployee: "$163,897", benefitsRate: "22%" },
    formulas: [
      { name: "Benefits", formula: "=base_salary * 0.22", desc: "22% of base" },
      { name: "Payroll Tax", formula: "=base_salary * 0.0765", desc: "FICA + Medicare" },
      { name: "Total per Role", formula: "=count * (base + benefits + tax + overhead)", desc: "All-in cost" },
    ],
    chart: {
      type: "bar",
      title: "Cost by role",
      xLabel: "Role",
      points: [
        { label: "Senior", value: 649968 },
        { label: "Mid", value: 657850 },
        { label: "Design", value: 290030 },
        { label: "PM", value: 177427 },
        { label: "Ops", value: 191496 },
      ],
    },
  },
];

function GridBackground() {
  return <div className="qfs-grid-bg" aria-hidden />;
}

function formatValue(value: number) {
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}K`;
  return String(value);
}

function MiniChart({ sheet }: { sheet: SpreadsheetScenario }) {
  const max = Math.max(...sheet.chart.points.map((p) => p.value), 1);
  const width = 220;
  const height = 110;
  if (sheet.chart.type === "line") {
    const coords = sheet.chart.points.map((p, i) => {
      const x = 12 + (i / Math.max(sheet.chart.points.length - 1, 1)) * (width - 24);
      const y = height - 18 - (p.value / max) * (height - 32);
      return { ...p, x, y };
    });
    const path = coords.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="qfs-chart-svg" role="img" aria-label={sheet.chart.title}>
        <path d={path} fill="none" stroke={C.GREEN} strokeWidth="2" />
        {coords.map((point) => <circle key={point.label} cx={point.x} cy={point.y} r="3" fill={C.GREEN} />)}
        {coords.map((point) => <text key={`${point.label}-label`} x={point.x} y={height - 4} textAnchor="middle">{point.label}</text>)}
      </svg>
    );
  }
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="qfs-chart-svg" role="img" aria-label={sheet.chart.title}>
      {sheet.chart.points.map((point, i) => {
        const barW = Math.max(14, (width - 30) / sheet.chart.points.length - 8);
        const x = 14 + i * ((width - 28) / sheet.chart.points.length);
        const h = (point.value / max) * (height - 34);
        const y = height - 18 - h;
        return (
          <g key={point.label}>
            <rect x={x} y={y} width={barW} height={h} rx="4" fill={C.GREEN} opacity="0.75" />
            <text x={x + barW / 2} y={height - 4} textAnchor="middle">{point.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function SpreadsheetCell({ value, header, selected, onClick, onDoubleClick, editing, onCommit, format }: {
  value: string;
  header?: boolean;
  selected?: boolean;
  editing?: boolean;
  format?: SpreadsheetRow["format"];
  onClick?: () => void;
  onDoubleClick?: () => void;
  onCommit?: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
  const isNumeric = /^[$\-+]?\d|%$|K$|M$|mo$/.test(value);
  return (
    <td className={["qfs-cell", header ? "qfs-cell-header" : "", selected ? "qfs-cell-selected" : ""].filter(Boolean).join(" ")} onClick={onClick} onDoubleClick={onDoubleClick} style={{ color: format?.color }}>
      {editing ? <input ref={inputRef} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => onCommit?.(draft)} onKeyDown={(e) => { if (e.key === "Enter") onCommit?.(draft); }} /> : <span className={isNumeric ? "qfs-numeric" : ""}>{value}</span>}
    </td>
  );
}

export default function FinanceSpreadsheetWorkroom({ onBack, onHome, sourceDivision, numberSources = DEFAULT_SOURCES, onExportXlsx, onSaveToSystem, onShare, onSendToGoogleSheets }: FinanceSpreadsheetWorkroomProps) {
  const [prompt, setPrompt] = useState("");
  const [activeSheet, setActiveSheet] = useState<SpreadsheetScenario | null>(null);
  const [generating, setGenerating] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{ r: number; c: number } | null>(null);
  const [editingCell, setEditingCell] = useState<{ r: number; c: number } | null>(null);
  const [savedTemplates, setSavedTemplates] = useState<SpreadsheetScenario[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => { const t = setInterval(() => setElapsed((p) => p + 1), 1000); return () => clearInterval(t); }, []);
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const cellName = selectedCell ? `${COLS[selectedCell.c] ?? ""}${selectedCell.r + 1}` : "—";
  const formulaText = useMemo(() => {
    if (!activeSheet || !selectedCell) return "";
    if (selectedCell.r === 0) return activeSheet.headers[selectedCell.c] ?? "";
    return activeSheet.rows[selectedCell.r - 1]?.cells[selectedCell.c] ?? "";
  }, [activeSheet, selectedCell]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 1700);
  };

  const buildSheet = (scenario: SpreadsheetScenario) => {
    setPrompt(scenario.prompt);
    setGenerating(true);
    setShowLibrary(false);
    window.setTimeout(() => {
      setActiveSheet(JSON.parse(JSON.stringify(scenario)) as SpreadsheetScenario);
      setGenerating(false);
      setSelectedCell(null);
      setEditingCell(null);
    }, 850);
  };

  const buildFromPrompt = () => {
    const text = prompt.toLowerCase();
    const scenario = text.includes("runway") || text.includes("burn") ? SCENARIOS[1] : text.includes("employee") || text.includes("salary") ? SCENARIOS[2] : SCENARIOS[0];
    buildSheet({ ...scenario, prompt: prompt.trim() || scenario.prompt });
  };

  const editCell = (r: number, c: number, value: string) => {
    if (!activeSheet || r <= 0) return;
    const rows = activeSheet.rows.map((row, i) => i === r - 1 ? { ...row, cells: row.cells.map((cell, idx) => idx === c ? value : cell) } : row);
    setActiveSheet({ ...activeSheet, rows });
    setEditingCell(null);
  };

  const saveTemplate = () => {
    if (!activeSheet) return;
    setSavedTemplates((prev) => [activeSheet, ...prev]);
    onSaveToSystem?.(activeSheet);
    showToast("Finance template saved locally · system save hook ready");
  };

  const handleAction = (kind: "export" | "share" | "google") => {
    if (!activeSheet) return;
    if (kind === "export") { onExportXlsx?.(activeSheet); showToast("Export hook ready · connect to existing .xlsx route"); }
    if (kind === "share") { onShare?.(activeSheet); showToast("Share is staged · connect Secure Share/Google route"); }
    if (kind === "google") { onSendToGoogleSheets?.(activeSheet); showToast("Google Sheets requires provider wiring"); }
  };

  return (
    <div className="qfs">
      <GridBackground />
      <header className="qfs-topbar">
        <div className="qfs-title-group">
          {onHome && <button type="button" onClick={onHome} className="qfs-home-q" title="Home — Square dashboard" aria-label="Home — Square dashboard">Q</button>}
          {onBack && <button type="button" onClick={onBack} className="qfs-back" title="Back to Command Center" aria-label="Back to Command Center">←</button>}
          <div className="qfs-icon">▦</div>
          <div>
            <span>FINANCE WORKROOM</span>
            <h1>Spreadsheet Builder</h1>
          </div>
          {sourceDivision && <em>{sourceDivision.name} hot button</em>}
          <span
            className="qfs-preview-badge"
            title="Preview. This spreadsheet builder runs locally in the browser — sheets, edits, and charts are not saved, exported, or sent anywhere until the export / save / share / Google Sheets seams are wired. Figures in the example scenarios are illustrative."
          >
            LOCAL PREVIEW · SIMULATED
          </span>
        </div>
        <div className="qfs-actions">
          <span className="qfs-session"><i />{String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}</span>
          {activeSheet && <button type="button" onClick={saveTemplate}>Save Template</button>}
          {activeSheet && <button type="button" onClick={() => setShowLibrary((p) => !p)}>Library ({savedTemplates.length})</button>}
          {activeSheet && <button type="button" onClick={() => handleAction("export")}>Export .xlsx</button>}
        </div>
      </header>

      {activeSheet && <div className="qfs-formula"><strong>{cellName}</strong><span>fx</span><input value={formulaText} readOnly /></div>}

      <main className="qfs-main">
        <section className="qfs-canvas">
          {!activeSheet && !generating && (
            <div className="qfs-empty">
              <div className="qfs-large-icon">▦</div>
              <h2>Turn finance ideas into a spreadsheet</h2>
              <p>Build from chat numbers, uploaded workspace files, Finance agent findings, or a new prompt. The first route is local/visual; repo hooks can bind it to workspace context, exports, secure share, and Google Sheets.</p>
              <div className="qfs-source-row">
                {numberSources.map((source) => (
                  <article key={source.id} className={`qfs-source-card qfs-source-${source.status}`}>
                    <strong>{source.label}</strong>
                    <span>{source.status === "available" ? "available" : source.status === "setup_required" ? "setup required" : "empty"}</span>
                    {source.values?.slice(0, 3).map((item) => <em key={item.label}>{item.label}: {item.unit === "$" ? "$" : ""}{item.value}{item.unit && item.unit !== "$" ? item.unit : ""}</em>)}
                  </article>
                ))}
              </div>
              <div className="qfs-scenarios">{SCENARIOS.map((scenario) => <button key={scenario.label} type="button" onClick={() => buildSheet(scenario)}><strong>{scenario.label}</strong><span>{scenario.rows.length} rows · {scenario.formulas.length} formulas</span></button>)}</div>
            </div>
          )}

          {generating && <div className="qfs-generating"><div /><strong>Building finance spreadsheet...</strong><span>Structuring rows, formulas, summary, and chart preview</span></div>}

          {activeSheet && !generating && (
            <div className="qfs-table-wrap">
              <table className="qfs-table">
                <thead><tr><th>#</th>{activeSheet.headers.map((h, i) => <SpreadsheetCell key={h} value={h} header selected={selectedCell?.r === 0 && selectedCell.c === i} onClick={() => setSelectedCell({ r: 0, c: i })} />)}</tr></thead>
                <tbody>{activeSheet.rows.map((row, ri) => <tr key={ri}><th>{ri + 1}</th>{row.cells.map((cell, ci) => <SpreadsheetCell key={`${ri}-${ci}`} value={cell} format={row.format} selected={selectedCell?.r === ri + 1 && selectedCell.c === ci} editing={editingCell?.r === ri + 1 && editingCell.c === ci} onClick={() => { setSelectedCell({ r: ri + 1, c: ci }); setEditingCell(null); }} onDoubleClick={() => setEditingCell({ r: ri + 1, c: ci })} onCommit={(value) => editCell(ri + 1, ci, value)} />)}</tr>)}</tbody>
              </table>
            </div>
          )}

          <div className="qfs-composer"><div><span>⌘</span><input value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && prompt.trim()) buildFromPrompt(); }} placeholder="Describe the finance model, forecast, budget, or numbers you want to see..." /></div><button type="button" onClick={buildFromPrompt}>Build</button></div>
        </section>

        {activeSheet && !showLibrary && <aside className="qfs-rail">
          <section><h3>Graph</h3><p>{activeSheet.chart.title}</p><MiniChart sheet={activeSheet} /> <div className="qfs-chart-values">{activeSheet.chart.points.map((p) => <span key={p.label}><b>{p.label}</b>{formatValue(p.value)}</span>)}</div></section>
          <section><h3>Summary</h3>{Object.entries(activeSheet.summary).map(([key, value]) => <div className="qfs-summary-row" key={key}><span>{key.replace(/([A-Z])/g, " $1").trim()}</span><strong>{value}</strong></div>)}</section>
          <section><h3>Formulas Used</h3>{activeSheet.formulas.map((formula) => <article className="qfs-formula-card" key={formula.name}><strong>{formula.name}</strong><code>{formula.formula}</code><span>{formula.desc}</span></article>)}</section>
          <section><h3>Output Actions</h3><button type="button" onClick={() => handleAction("export")}>Export .xlsx</button><button type="button" onClick={() => handleAction("share")}>Save / Share to system</button><button type="button" onClick={() => handleAction("google")}>Send to Google Sheets · setup required</button></section>
        </aside>}

        {showLibrary && <aside className="qfs-rail"><section><h3>Saved Finance Templates</h3>{savedTemplates.length === 0 ? <p>No saved templates yet. Save a model to reuse formulas and headers.</p> : savedTemplates.map((sheet, i) => <article className="qfs-template-card" key={`${sheet.label}-${i}`} onClick={() => setActiveSheet(sheet)}><strong>{sheet.label}</strong><span>{sheet.headers.slice(0, 4).join(" · ")}</span></article>)}<button type="button" onClick={() => setShowLibrary(false)}>Close Library</button></section></aside>}
      </main>
      {toast && <div className="qfs-toast">{toast}</div>}
    </div>
  );
}
